import { useEffect, useCallback } from 'react';
import { CallState } from '../../types';
import { useSAPContactSearch } from '../useSAPContactSearch';
import { useManualSapContactSearch } from './useManualSapContactSearch';
import { useSapAutoOpenContact } from './useSapAutoOpenContact';

interface UseSapCallLinkingOptions {
  callState: CallState;
  isRinging: boolean;
  /** Click-to-dial SAP contact id from outbound dial event. */
  preferredContactId?: string | null;
  /** Click-to-dial reference reused by outbound SAP ACCEPT and END. */
  preferredReferenceId?: string | null;
}

export function shouldAutoSearchSapContacts(callState: CallState, isRinging: boolean): boolean {
  if (!callState.callerNumber) {
    return false;
  }

  if (isRinging) {
    return true;
  }

  // Also search once the call is live, not just while ringing: CXone maps
  // outbound dial progress to `connecting` (never `ringing`), and auto-accept
  // inbound calls can jump straight to `active` — handleAutoAcceptCall flips
  // isRinging false and status to active together, so isRinging may never
  // render true at all for those calls.
  return (
    callState.status === 'connecting' ||
    callState.status === 'active' ||
    callState.status === 'holding'
  );
}

export function useSapCallLinking({
  callState,
  isRinging,
  preferredContactId = null,
  preferredReferenceId = null,
}: UseSapCallLinkingOptions) {
  const {
    contactState: sapContactState,
    searchContacts,
    clearContacts,
    isEnabled: isSapC4cEnabled,
  } = useSAPContactSearch();

  const { manualSearchState, handleManualSearch, resetManualSearch } = useManualSapContactSearch();
  const { openedSapContacts, setOpenedSapContacts, handleSapContactOpened, resetOpenedContacts } =
    useSapAutoOpenContact({
      callState,
      isSapC4cEnabled,
      sapContactState,
      preferredContactId,
      preferredReferenceId,
    });

  useEffect(() => {
    if (isSapC4cEnabled && shouldAutoSearchSapContacts(callState, isRinging)) {
      searchContacts(callState.callerNumber!);
    }
    // Intentionally depend on call fields used by shouldAutoSearchSapContacts, not full callState.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callState object identity changes every render
  }, [
    isRinging,
    callState.callerNumber,
    callState.direction,
    callState.status,
    isSapC4cEnabled,
    searchContacts,
  ]);

  const resetSapLinking = useCallback(() => {
    resetOpenedContacts();
    resetManualSearch();
    clearContacts();
  }, [resetOpenedContacts, resetManualSearch, clearContacts]);

  return {
    sapContactState,
    isSapC4cEnabled,
    manualSearchState,
    openedSapContacts,
    setOpenedSapContacts,
    handleSapContactOpened,
    handleManualSearch,
    resetSapLinking,
  };
}
